import { useEffect, useLayoutEffect, useState } from 'react';
import {
  useAnalyticsPost,
  NarvarClickTypes,
  NarvarClickSubTypes,
  NarvarEventComponents,
  NarvarEventTypes,
} from '@narvar/nth-hook-context-analytics';
import { useCustomComponentStateContext } from '../hooks/useCustomComponentState';

function Script() {
  const { state, signalContentLoaded } = useCustomComponentStateContext();
  const logEvent = useAnalyticsPost();

  const script =
    (state?.queryData?.retailerSettings?.productRecommendations?.type ===
      'SCRIPT' &&
      state?.queryData?.retailerSettings?.productRecommendations.script) ||
    (state?.settings &&
      typeof state?.settings.script === 'string' &&
      state?.settings.script);

  const {
    clickCategory: settingsClickCategory,
    clickSubType: settingsClickSubType,
    feature: settingsFeature,
  } = (state?.settings && state?.settings) || {};

  // FIXME: This flag ensures that the custom product recommendations script set
  // in hub is only fired a single time. The script is sent to this applicaiton
  // via iFrame PostMessage. We have logic in the parent window that updates the
  // pageContext state during scroll or window resize that initiates a new
  // postMessage event to be sent to this application and caused the below
  // useLayoutEffect to fire (erroneously running the script multiple times).
  // A long term fix might look like separating the custom script PostMessage
  // from the page context PostMessage.
  const [isScriptRun, setIsScriptRun] = useState(false);

  useLayoutEffect(() => {
    if (typeof script !== 'string') return;
    if (isScriptRun) return;

    try {
      // This should be iframed and "safe" to run arbitrary JS
      // eslint-disable-next-line no-new-func, @typescript-eslint/no-implied-eval
      const f = new Function('state', 'signalContentLoaded', script);
      f(state, signalContentLoaded);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }

    setIsScriptRun(true);
  }, [script, isScriptRun, state, signalContentLoaded]);

  useEffect(() => {
    const listener = (
      event: MouseEvent & {
        target:
          | (EventTarget & {
              tagName?: undefined;
              href?: undefined;
              dataset?: undefined;
            })
          | HTMLAnchorElement
          | null;
      },
    ) => {
      const { tagName, href, dataset } = event?.target || {};

      const clickType =
        tagName === 'A' && typeof href === 'string'
          ? NarvarClickTypes.OUTGOING
          : NarvarClickTypes.INTERACTION;

      let clickCategory: string = NarvarClickSubTypes.PRODUCT_RECOMMENDATIONS;
      if (dataset && dataset.narvarClickCategory) {
        clickCategory = dataset.narvarClickCategory;
      } else if (typeof settingsClickCategory === 'string') {
        clickCategory = settingsClickCategory;
      }

      let clickSubType: string = NarvarClickSubTypes.PRODUCT_RECOMMENDATIONS;
      if (dataset && dataset.narvarClickSubType) {
        clickSubType = dataset.narvarClickSubType;
      } else if (typeof settingsClickSubType === 'string') {
        clickSubType = settingsClickSubType;
      }

      let feature = 'productRecommendations';
      if (dataset && dataset.narvarFeature) {
        feature = dataset.narvarFeature;
      } else if (typeof settingsFeature === 'string') {
        feature = settingsFeature;
      }

      logEvent({
        clickCategory,
        clickLinkDestinationUrl: href,
        clickLinkLabel: href,
        clickSubType,
        clickType,
        eventComponent: NarvarEventComponents.NTH_BLOCK_CUSTOM_COMPONENT,
        eventFeature: NarvarEventComponents.NTH_BLOCK_CUSTOM_COMPONENT,
        eventType: NarvarEventTypes.CLICK,
        feature,
        pageComponent: NarvarEventComponents.NTH_BLOCK_CUSTOM_COMPONENT,
      });
    };

    document.body.addEventListener('click', listener);
    return () => document.body.removeEventListener('click', listener);
  }, [settingsClickCategory, settingsClickSubType, settingsFeature, logEvent]);

  return null;
}

export default Script;
